import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Item, ItemContent, ItemGroup, ItemTitle } from '@/components/ui/item';
import type { OverzichtData } from './overzicht.api';

const keer = (n: number) => `${n} ${n === 1 ? 'keer' : 'keer'}`;
const inGesprekken = (n: number) => `in ${n} ${n === 1 ? 'gesprek' : 'gesprekken'}`;

/**
 * Hoofdonderwerpen op aantal, uitklapbaar naar de deelonderwerpen eronder.
 *
 * Een accordeon en geen tabel: shadcn levert geen tabel met uitklapbare rijen,
 * dus dat zou zelf bouwen worden — en dit is toetsenbord-toegankelijk zonder
 * dat er iets aan hoeft.
 */
export function TopOnderwerpenKaart({ data }: { data: OverzichtData }) {
  const { topOnderwerpen, periode } = data;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meest terugkerende hoofdonderwerpen</CardTitle>
        <CardDescription>
          Klap een onderwerp uit voor de deelonderwerpen eronder · {periode.label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {topOnderwerpen.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nog geen onderwerpen</EmptyTitle>
              <EmptyDescription>
                In deze periode is er nog niets vastgesteld om te kunnen ordenen.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <Accordion collapsible type="single">
            {topOnderwerpen.map((onderwerp) => (
              <AccordionItem key={onderwerp.id} value={onderwerp.id}>
                <AccordionTrigger>
                  <span className="flex flex-1 items-center justify-between gap-4 pr-2">
                    <span className="font-medium">{onderwerp.label}</span>
                    <span className="flex items-center gap-2 text-muted-foreground">
                      {/* Op een telefoon eruit: bij een lange onderwerpnaam valt
                          dit over twee regels en duwt het de rij uit elkaar. Het
                          aantal ernaast blijft staan, en de breedte staat
                          uitgesplitst in de kaart hiernaast. */}
                      <span className="hidden text-sm sm:inline">
                        {inGesprekken(onderwerp.gesprekken)}
                      </span>
                      <Badge variant="secondary">{onderwerp.aantal}</Badge>
                    </span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <ItemGroup>
                    {onderwerp.deelonderwerpen.map((deel) => (
                      <Item key={deel.id} size="sm" variant="outline">
                        <ItemContent>
                          <ItemTitle>{deel.label}</ItemTitle>
                        </ItemContent>
                        <span className="flex items-center gap-2 text-sm text-muted-foreground">
                          {keer(deel.aantal)}
                          {/* Zelfde reden als bij de naam hierboven: op een
                              telefoon is dit de regel die de rij breekt. */}
                          <span className="hidden sm:inline">
                            · {inGesprekken(deel.gesprekken)}
                          </span>
                        </span>
                      </Item>
                    ))}
                  </ItemGroup>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
}
